import type {RepositoryNWO} from '@github-ui/current-repository'
import type {ButtonProps} from '@primer/react'
import {useCallback, useState} from 'react'

import {ChecksStatusBadge} from './ChecksStatusBadge'
import {useCommitChecksStatusDetails} from './hooks/use-commit-checks-status-details'

interface LiveChecksStatusBadgeProps {
  oid: string
  statusRollup: string | undefined
  repo: RepositoryNWO
  aliveChannel?: string | null
  variant?: 'circled' | 'filled' | 'default'
  size?: ButtonProps['size']
  descriptionText?: string
  buttonClassName?: ButtonProps['className']
  buttonVariant?: ButtonProps['variant']
  iconOnly?: boolean
  ariaLabel?: string
}

/**
 * A badge component that displays the status of commit checks and allows users to view detailed information in a popover.
 * When clicked, it opens a popover and fetches detailed information about the checks.
 * When provided with an `aliveChannel`, the dialog live updates to the checks status and updates the popover content.
 */
export function LiveChecksStatusBadge({
  oid,
  statusRollup,
  repo,
  aliveChannel,
  variant,
  size = 'small',
  descriptionText,
  buttonClassName,
  buttonVariant,
  iconOnly,
  ariaLabel,
}: LiveChecksStatusBadgeProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [details, fetchDetails] = useCommitChecksStatusDetails(oid, repo, {
    aliveChannel,
    isDialogOpen,
  })
  const repositoryNwo = `${repo.ownerLogin}/${repo.name}`

  const handleDialogOpenChange = useCallback((open: boolean) => {
    setIsDialogOpen(open)
  }, [])

  if (!statusRollup) return null

  return (
    <ChecksStatusBadge
      statusRollup={statusRollup}
      combinedStatus={details}
      onWillOpenPopup={fetchDetails}
      onDialogOpenChange={handleDialogOpenChange}
      variant={variant}
      size={size}
      descriptionText={descriptionText}
      buttonClassName={buttonClassName}
      buttonVariant={buttonVariant}
      iconOnly={iconOnly}
      ariaLabel={ariaLabel}
      disablePopover={false}
      repositoryNwo={repositoryNwo}
    />
  )
}
